import React from 'react';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';
import attributes from '../constants/attributes';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';

const { Paragraph, Icon } = designSystem;
const isMobile = bowser.mobile || bowser.tablet;

const SLIDER_MIN = 0;
const SLIDER_MAX = 128;
const SLIDER_STEP = 1;
const RESTRICTED_VALUE = 32;

const SPACINGS = {
  left: {
    id: 'left-spacing',
    attributes: [attributes.PADDING_LEFT],
    iconGlyph: 'padding-left',
  },
  right: {
    id: 'right-spacing',
    attributes: [attributes.PADDING_RIGHT],
    iconGlyph: 'padding-right',
  },
};

const getPopoverProps = ({ imgSrc, title, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {title}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

const getValuePopoverProps = (restrictedValue) => ({
  renderContent: () => {
    const desktopMessage = i18next.t(
      'Note: Padding over {value} will be scaled down on tablets and mobile devices.',
      { value: `${restrictedValue}px` }
    );
    const mobileMessage = i18next.t('Note: Padding over {value} will only be shown on desktop.', {
      value: `${restrictedValue}px`,
    });

    return (
      <Paragraph appearance="white" size="small">
        {isMobile ? mobileMessage : desktopMessage}
      </Paragraph>
    );
  },
  checkVisibility: (value) => value > restrictedValue,
});

const getHorizontalSpacing = (settingId, settings = {}) => ({
  ...settings,
  id: controlIds.HORIZONTAL_SPACING,
  title: i18next.t('Left&Right'),
  attributes: Object.values(SPACINGS).flatMap(({ attributes: attrs }) => attrs),
  iconGlyph: 'padding-horizontal',
  computeFallbackValue: computeSpacingFallback,
  mixedLabel: i18next.t('Mixed'),
  step: SLIDER_STEP,
  min: SLIDER_MIN,
  max: SLIDER_MAX,
  items: Object.values(SPACINGS).map((item) => ({
    ...item,
    computeFallbackValue: computeSpacingFallback,
    valuePopover: getValuePopoverProps(RESTRICTED_VALUE),
  })),
  popover: getPopoverProps({
    imgSrc: previewGifs.COLUMN_HORIZONTAL_PADDING_GIF,
    title: i18next.t('Adjust the left and right spaces inside columns'),
    iconGlyph: 'padding-horizontal',
  }),
  valuePopover: getValuePopoverProps(RESTRICTED_VALUE),
});

export default getHorizontalSpacing;
